import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import {
  useCreateUnitConversion,
  useUpdateUnitConversion,
} from "@/hooks/use-unit-conversions";
import { unitConversionService } from "@/services/unit-conversion";

import {
  defaultUnitConversionValues,
  unitConversionSchema,
  type UnitConversionFormValues,
} from "./unit-conversion-schema";

import type {
  ConversionImpactPreview,
  UnitConversion,
  UnitOfMeasurement,
} from "@artisancode/api-types";

export function useUnitConversionForm(
  open: boolean,
  editing: UnitConversion | null,
  uoms: UnitOfMeasurement[],
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateUnitConversion();
  const { mutate: update, isPending: isUpdating } = useUpdateUnitConversion();
  const { data: me } = useMe();

  const [impactPreview, setImpactPreview] =
    useState<ConversionImpactPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const form = useForm<UnitConversionFormValues>({
    resolver: zodResolver(unitConversionSchema),
    defaultValues: defaultUnitConversionValues,
  });

  useEffect(() => {
    if (open) {
      const fromUnit = uoms.find((u) => u.id === editing?.fromUnitId);
      form.reset(
        editing
          ? {
              category: fromUnit?.category ?? "",
              fromUnitId: editing.fromUnitId,
              toUnitId: editing.toUnitId,
              factor: String(editing.factor),
            }
          : {
              ...defaultUnitConversionValues,
              workspaceId: me?.workspaceId ?? "",
            },
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImpactPreview(null);
    } else {
      setImpactPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  const category = useWatch({ control: form.control, name: "category" });
  const unitsInCategory = useMemo(
    () =>
      uoms.filter(
        (u) =>
          u.category === category &&
          (u.isActive ||
            u.id === editing?.fromUnitId ||
            u.id === editing?.toUnitId),
      ),
    [uoms, category, editing],
  );

  function selectCategory(value: string) {
    form.setValue("category", value);
    form.setValue("fromUnitId", "");
    form.setValue("toUnitId", "");
  }

  async function onSubmit(values: UnitConversionFormValues) {
    const factor = Number(values.factor);
    if (editing) {
      // If factor changed and we haven't previewed/confirmed yet, check recipe impact first
      if (Number(editing.factor) !== factor && !impactPreview) {
        setIsLoadingPreview(true);
        try {
          const preview = await unitConversionService.previewImpact(
            editing.id,
            factor,
          );
          if (preview.affectedCount > 0) {
            setImpactPreview(preview);
            setIsLoadingPreview(false);
            return;
          }
        } catch (err: unknown) {
          const error = err as Error;
          toast.error(error?.message || "Gagal memeriksa dampak konversi.");
          setIsLoadingPreview(false);
          return;
        }
        setIsLoadingPreview(false);
      }

      update(
        {
          id: editing.id,
          fromUnitId: values.fromUnitId,
          toUnitId: values.toUnitId,
          factor,
          confirmed: true,
        },
        {
          onSuccess: () => {
            toast.success("Konversi satuan berhasil diperbarui.");
            setImpactPreview(null);
            onSuccess();
          },
          onError: (err: Error) => {
            toast.error(err?.message || "Gagal memperbarui konversi satuan.");
          },
        },
      );
    } else {
      create(
        {
          fromUnitId: values.fromUnitId,
          toUnitId: values.toUnitId,
          factor,
          workspaceId: values.workspaceId || undefined,
        },
        {
          onSuccess: () => {
            toast.success("Konversi satuan berhasil ditambahkan.");
            onSuccess();
          },
          onError: (err: Error) => {
            toast.error(err?.message || "Gagal menambahkan konversi satuan.");
          },
        },
      );
    }
  }

  function cancelPreview() {
    setImpactPreview(null);
  }

  return {
    form,
    category,
    unitsInCategory,
    selectCategory,
    impactPreview,
    isLoadingPreview,
    cancelPreview,
    isSubmitting: isCreating || isUpdating || isLoadingPreview,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
